/**
 * Single source of truth for status vocabulary shared across admin and
 * player views. Pages must import these instead of redefining local maps —
 * the audit found the same status rendered with different words per page
 * (e.g. cancelled as "Cancelado" in Finanzas but "Rechazado" in
 * Inscripciones).
 */

export const MATCH_STATUS_LABELS: Record<string, string> = {
  scheduled: "Programado",
  in_progress: "En curso",
  completed: "Completado",
  cancelled: "Cancelado",
  walkover: "Walkover",
  no_show: "No se presentó",
  defaulted: "Default",
  retired: "Retiro",
  postponed: "Pospuesto",
  double_walkover: "Doble W.O.",
  bye: "Descanso",
};

export function matchStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return MATCH_STATUS_LABELS[status] ?? status;
}

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente de pago",
  paid: "Pago confirmado",
  payment_sent: "Pago enviado",
  cancelled: "Rechazado",
  refunded: "Reembolsado",
  receipt_submitted: "Comprobante enviado",
  pending_receipt: "Pendiente de comprobante",
  rejected: "Rechazado",
  failed: "Fallido",
  expired: "Vencido",
};

// Nunca se muestra un estado crudo en inglés (receipt_submitted, etc.).
export const UNKNOWN_STATUS_LABEL = "Otro estado";

export function paymentStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return PAYMENT_STATUS_LABELS[status] ?? UNKNOWN_STATUS_LABEL;
}

// Player-facing payment status wording, used on the public order/payment page.
// Keep this wording distinct from the admin-facing PAYMENT_STATUS_LABELS above
// (e.g. players see "Pagado" while admins see "Pago confirmado").
export const PUBLIC_PAYMENT_STATUS_LABELS: Record<string, string> = {
  paid: "Pagado",
  pending: "Pendiente",
  refunded: "Reembolsado",
  cancelled: "Cancelado",
};

export function publicPaymentStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return PUBLIC_PAYMENT_STATUS_LABELS[status] ?? "—";
}

export const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  mercadopago_marketplace: "Mercado Pago",
  stripe_connect: "Stripe",
  manual: "Manual",
  manual_club: "Club manual",
};

export function paymentProviderLabel(provider?: string | null): string {
  if (!provider) return "—";
  return PAYMENT_PROVIDER_LABELS[provider] ?? provider;
}

// Club membership request lifecycle — shared by the club panel and the player
// marketplace so the same state never reads differently on each side.
export const MEMBERSHIP_REQUEST_STATUS_LABELS: Record<string, string> = {
  pending_review: "Pendiente de revisión",
  approved_active: "Aprobada · socio activo",
  approved_payment_pending: "Aprobada · pago pendiente",
  rejected: "Rechazada",
  cancelled: "Cancelada",
};

export function membershipRequestStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return MEMBERSHIP_REQUEST_STATUS_LABELS[status] ?? status;
}

// Club membership manual-payment order lifecycle.
export const MEMBERSHIP_PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending_receipt: "Esperando comprobante",
  receipt_submitted: "Comprobante enviado",
  paid: "Pagado",
  rejected: "Rechazado",
};

export function membershipPaymentStatusLabel(status?: string | null): string {
  if (!status) return "—";
  return MEMBERSHIP_PAYMENT_STATUS_LABELS[status] ?? PAYMENT_STATUS_LABELS[status] ?? UNKNOWN_STATUS_LABEL;
}

export const SPORT_LABELS: Record<string, string> = {
  tenis: "Tenis",
  padel: "Pádel",
  pickleball: "Pickleball",
};

// A diferencia de matchStatusLabel/paymentStatusLabel (que caen a "—"),
// aquí el fallback es tenis: un torneo/cancha sin sport es dato legado
// previo al multideporte, y en la práctica ES de tenis — no un desconocido.
export function sportLabel(sport?: string | null): string {
  if (!sport) return SPORT_LABELS.tenis;
  return SPORT_LABELS[sport] ?? sport;
}

/**
 * Vocabulario de marcador por deporte. En pickleball la unidad mayor es el
 * game (a 11 puntos) y la menor el punto; el modelo interno no cambia, solo
 * las palabras que ve el usuario. setsHeader/ggHeader/gpHeader alimentan los
 * encabezados de las tablas de standings (hoy "Sets"/"GG"/"GP").
 */
export interface ScoreVocab {
  setsHeader: string;
  ggHeader: string;
  gpHeader: string;
  ggGpLegend: string;
}

export const SCORE_VOCAB: Record<string, ScoreVocab> = {
  tenis: {
    setsHeader: "Sets",
    ggHeader: "GG",
    gpHeader: "GP",
    ggGpLegend: "games a favor, contra y diferencial",
  },
  padel: {
    setsHeader: "Sets",
    ggHeader: "GG",
    gpHeader: "GP",
    ggGpLegend: "games a favor, contra y diferencial",
  },
  pickleball: {
    setsHeader: "Games",
    ggHeader: "PG",
    gpHeader: "PP",
    ggGpLegend: "puntos ganados, perdidos y diferencial",
  },
};

export function scoreVocab(sport?: string | null): ScoreVocab {
  return SCORE_VOCAB[sport ?? "tenis"] ?? SCORE_VOCAB.tenis;
}

export function scoreSetLabel(index: 0 | 1, sport?: string | null): string {
  return sport === "pickleball" ? `Game ${index + 1}` : `Set ${index + 1}`;
}

export function scoreDeciderLabel(
  sport?: string | null,
  useSuperTiebreak = false,
): string {
  if (sport === "pickleball") return "Game 3";
  return useSuperTiebreak ? "TB" : "Set 3";
}
